import React from "react";
import ArticleTemplate from "../../../components/ArticleTemplate";

const PhishingAttacks = () => {
    return (
        <ArticleTemplate
            title="The Human Weakness in a Decentralised World: Phishing Attacks and Social Engineering in Web3"
            subtitle="Why human psychology is the biggest vulnerability in Web3 security."
            author="Extropy Team"
            date="October 7th, 2025"
            readingTime="9 min read"
            tags={["Blockchain", "Security", "Smart Contracts"]}
        >
            <p>
                Web3 represents a massive leap forward in how we interact with the
                internet, money, and digital identity. The technology has been evolving,
                the security layer has been getting stronger, and yet regardless of all
                the cryptographic sophistication, one timeless vulnerability remains:
                human psychology. When it comes to crypto scams, the weakest link is not
                always the software, it's the people.
            </p>
            <p>
                Attackers don't always need to crack the code; they just need to trick a
                person into handing over access. This is where phishing and social
                engineering thrive.
            </p>
            <p>
                As Web3 grows into a multi-trillion-dollar ecosystem, phishing attacks
                have become one of its most consistent and damaging threats. These scams
                exploit trust, curiosity, fear, or opportunity to bypass security
                entirely. In this article, we'll explore how phishing and social
                engineering have evolved from Web2 into the Web3 landscape, their unique
                impacts, and what the community can do to build resilience against them.
            </p>

            <h2>The Roots of Phishing and Social Engineering in Web2</h2>
            <p>
                Almost every internet user has either heard of phishing scams or knows
                someone who has fallen victim to them, if not experienced it themselves.
                Phishing first gained traction in the early days of the internet, when
                attackers would send fraudulent emails posing as trusted institutions
                like banks, urging users to verify their credentials. A single click on
                a malicious link or the entry of a password on a fake site often gave
                criminals direct access to accounts and sometimes that would lead to
                access to private or financial information.
            </p>
            <p>
                Social engineering soon expanded far beyond email. Attackers began to
                employ a variety of psychological tactics designed to exploit human
                behaviour rather than technical flaws.
            </p>
            <p>
                Pretexting involved fabricating believable scenarios such as pretending
                to be a company employee or IT administrator to extract sensitive
                information. Baiting relied on curiosity or greed, offering something
                enticing like free software, USB drives, or downloads that actually
                contained malware. Fake tech support calls played on fear and urgency,
                with scammers convincing victims that their systems were compromised and
                guiding them to "fix" the issue by installing malicious software or
                revealing credentials. Spear-phishing campaigns took things further by
                tailoring messages to specific individuals or organizations, making them
                far more convincing than generic spam.
            </p>
            <p>
                Despite the differences in method, the formula remained consistent:
                exploit trust, create a sense of legitimacy or urgency, and manipulate
                targets into willingly surrendering private or financial data.
            </p>
            <p>
                In Web2, victims often had a fallback plan they can lean into in some
                specific scenarios. Sometimes banks could reverse fraudulent
                transactions, and platforms had centralised customer service departments
                to help restore compromised accounts. However that doesn't exist within
                the echoes of Web3.
            </p>

            <h2>How Phishing Evolved in the Web3 Landscape</h2>
            <p>
                In Web3, control of funds and identity is inseparable from cryptographic
                keys. Whoever holds the private key has full authority over the
                associated wallet and its assets. This means that a single phishing
                mistake (whether revealing a seed phrase or approving the wrong
                transaction) can result in permanent, irreversible loss. Unlike Web2,
                there are no customer support hotlines or fraud departments to appeal
                to. Once assets move on-chain, they are gone.
            </p>
            <p>
                Attackers understand this, and instead of trying to brute-force
                cryptography, they target human behaviour. The most common approach is
                to convince a user to willingly hand over access, often under the
                illusion of a legitimate action.
            </p>
            <p>Phishing has therefore adapted to the new environment of Web3:</p>
            <p>
                <strong>Fake decentralised applications (dApps):</strong> Lookalike
                websites mimic legitimate platforms, tricking users into connecting
                wallets and signing malicious contracts.
            </p>
            <p>
                <strong>Malicious wallet popups:</strong> Attackers exploit confusing
                transaction prompts, where signing unreadable hexadecimal code can
                unknowingly grant unlimited token approvals.
            </p>
            <p>
                <strong>Fraudulent community announcements:</strong> Discord and
                Telegram, central hubs for Web3 projects, are frequent targets. Hackers
                compromise admin accounts or create convincing clones, then broadcast
                fake links to airdrops, staking pools, or exclusive mints.
            </p>
            <p>
                <strong>Twitter scams:</strong> Verified or hacked accounts of
                well-known projects, founders, or influencers post urgent official
                announcements often about fake partnerships, surprise token launches, or
                emergency migrations. In other cases, attackers create accounts
                impersonating well-known figures, using similar usernames, profile
                pictures, and bios to appear authentic. These posts spread quickly
                through retweets and replies, amplifying their legitimacy and leading to
                mass wallet drains within minutes.
            </p>
            <p>
                Another unique challenge is the pseudonymous and decentralised nature of
                Web3 communities. Without real-world verification, attackers can easily
                impersonate project founders, moderators, or trusted partners. A fake
                "admin" offering to help with a wallet issue or a "developer" posting an
                urgent update can often deceive even experienced users. And because
                there's no central authority or platform owner responsible for resolving
                scams, victims are left largely without recourse.
            </p>
            <p>
                Phishing in Web3 is not just a continuation of Web2 tactics, it's a
                reimagining of social engineering for a trustless ecosystem, where human
                error can completely bypass even the strongest cryptography.
            </p>

            <h2>The Role of Social Engineering in Web3 Exploits</h2>
            <p>
                While phishing describes how attackers deliver malicious links or
                contracts, social engineering explains why users fall for them. Most
                major Web3 exploits start not with code, but with human behaviour.
                Attackers understand that in decentralised spaces, people lean heavily
                on trust, urgency, and insider access and they weaponise those
                instincts.
            </p>
            <p>Some of the most common psychological levers include:</p>
            <p>
                <strong>Trust in Community Figures:</strong> Users often treat founders,
                moderators, or "OG" members as authorities. When these identities are
                impersonated or hacked, people are quick to comply.
            </p>
            <p>
                <strong>Fear of Missing Out:</strong> Scarcity and hype drive much of
                Web3 activity. Announcements like "only 500 spots left in this mint"
                push people to act before thinking.
            </p>
            <p>
                <strong>Fear and Urgency:</strong> Warnings such as "your wallet may be
                compromised" or "account suspended unless you verify" override rational
                caution.
            </p>
            <p>
                <strong>Exclusivity &amp; Opportunity:</strong> Pitches for "private
                token sales," "whitelist spots," or "special staking pools" prey on
                ambition and greed.
            </p>
            <p>
                These psychological triggers make scams highly effective, even against
                experienced users. Unlike Web2, where scams often target isolated
                individuals, Web3 scams exploit community trust loops: when one person
                in a Discord server clicks, dozens of others may follow without
                question.
            </p>

            <h2>The Impact of Phishing on the Web3 Space So Far</h2>
            <p>
                Phishing in Web3 has evolved from a nuisance into a major threat, both
                financially and socially, draining hundreds of millions in 2025 alone
                and undermining trust in the decentralised ecosystem. Recent data from
                blockchain security firm CertiK highlights the scale of the problem: in
                the first half of 2025, crypto hacks and scams cost investors $2.47
                billion, already surpassing the total stolen in all of 2024 ($2.42
                billion). Of that amount, $1.7 billion resulted from wallet compromises,
                while $410 million was stolen specifically through phishing attacks
                across 132 documented incidents.
            </p>
            <p>
                Two headline-grabbing events: 'the $1.5 billion Bybit hack and the $220
                million Cetus Protocol exploit' accounted for nearly 72% of all losses,
                but phishing remains a widespread threat, targeting not just exchanges
                but everyday users across Web3 communities.
            </p>
            <p>Some of the high-profile incidents illustrate the stakes:</p>
            <ul>
                <li>
                    Fake OpenSea links circulated in hacked Discord servers tricked users
                    into signing wallet-draining approvals.
                </li>
                <li>
                    Twitter and Discord account takeovers of popular NFT projects led
                    entire communities to mint counterfeit tokens.
                </li>
                <li>
                    Airdrop and giveaway scams drained wallets immediately upon
                    interaction with malicious smart contracts.
                </li>
            </ul>
            <p>
                The financial impact is severe, but the reputational damage is equally
                concerning. Each successful phishing attack erodes trust within the
                ecosystem, discourages newcomers, and amplifies the perception that Web3
                is unsafe. In a sector that depends on community participation, social
                and financial consequences are tightly intertwined.
            </p>
            <p>
                In essence, these incidents highlight a critical truth: technical
                security alone cannot protect users. Even the strongest cryptography is
                powerless against attacks that exploit Human psychology, trust, and the
                eagerness to participate in decentralised communities.
            </p>

            <h2>Web3 Job Hunting: The New Frontier for Phishing Scams</h2>
            <p>
                One of the fastest-growing phishing scams in Web3 at the moment isn't
                happening on exchanges or with fake coin launches, it's in the job
                market. As demand for blockchain talent surges, and with more talent
                getting into the industry seeking new opportunities, attackers have
                learned to weaponise fake opportunities and prey on job seekers,
                exploiting eager developers, marketers, and researchers with convincing
                recruitment scams.
            </p>
            <p>
                The remote-first, global nature of the industry makes it easy for
                fraudsters to impersonate recruiters, HR managers, or even entire
                companies. The payoff is high: malware installs, stolen credentials, or
                drained wallets.
            </p>

            <h3>Common Job Scam Tactics in Web3</h3>
            <p>
                <strong>Fake Job Postings &amp; Imposter Recruiters:</strong> Scammers
                clone company websites or create LinkedIn profiles that mirror real HR
                staff. Applicants are funneled to malicious forms or onboarding docs
                carrying spyware.
            </p>
            <p>
                <strong>Fraudulent Interviews &amp; Smart Contracts:</strong> Attackers
                stage video calls and follow up with fake NDAs or "offer letters"
                disguised as smart contracts that's designed to drain wallets on
                signing.
            </p>
            <p>
                <strong>Malware via 'Test Tasks':</strong> Applicants are asked to
                complete coding challenges or download "trial software," which often
                contains stealers like Atomic Stealer or RATs that compromise systems.
                They either ask you before the interview to do a task as an initial test
                downloading a github repo, or asking you that in the middle of the
                interview pressuring you into instantly installing it.
            </p>
            <p>
                <strong>Deposit &amp; Training Fee Scams:</strong> Victims are told they
                must pre-pay for "security clearance" or "training modules." In 2024,
                Canada's Anti-Fraud Centre recorded losses ranging from $600–$1,300 per
                victim.
            </p>
            <p>
                <strong>Pig Butchering Disguised as Jobs:</strong> Roles like "crypto
                trading assistant" start with small payouts, but candidates are later
                pressured to deposit large sums into fraudulent platforms.
            </p>

            <h3>Why These Scams Work</h3>
            <p>
                Crypto jobs promise high salaries, remote flexibility, and interesting
                projects to work on, all of that sets the ideal conditions for scammers.
                Combined with fake urgency ("limited slots"), authority ("we're hiring
                for [big protocol]"), and exclusivity ("private roles, invite-only"),
                applicants often suspend skepticism until it's too late.
            </p>

            <h3>Precautions you should take in consideration as a Web3 job seeker</h3>
            <ul>
                <li>
                    <strong>Verify the source:</strong> Cross-check job postings on the
                    official careers page of the company. Don't rely solely on LinkedIn,
                    Telegram, or Discord messages.
                </li>
                <li>
                    <strong>Investigate recruiter profiles:</strong> Confirm recruiter
                    identities by checking company directories, LinkedIn connections, or
                    direct outreach to the company's HR team. Few common red flags are
                    newly created profiles, vague job descriptions, unrealistic high
                    salary that doesn't fit the position, or suspicious companies with
                    barely any work published.
                </li>
                <li>
                    <strong>Never share seed phrases or private keys:</strong> No
                    legitimate employer will ever ask for wallet access, seed phrases, or
                    transaction signatures.
                </li>
                <li>
                    <strong>Be cautious with 'test tasks':</strong> Avoid downloading
                    unknown files or executables. Ask for tasks that can be completed in a
                    controlled environment (e.g., GitHub repo, online editor).
                </li>
                <li>
                    <strong>Check contract documents carefully:</strong> Be wary of "offer
                    letters" or NDAs in smart contract form. Employment agreements should
                    not require wallet signatures.
                </li>
                <li>
                    <strong>Watch for urgency and pressure:</strong> Scammers often push
                    you to act quickly and induce a fear of missing out. Legitimate
                    companies give candidates time to review details and think the job
                    offer thoroughly.
                </li>
                <li>
                    <strong>Use a separate wallet for job interactions:</strong> If a
                    recruiter insists on a wallet signature for "identity verification"
                    that's already a huge red flag, However make sure you use a clean
                    wallet with no funds.
                </li>
                <li>
                    <strong>Confirm interview channels:</strong> Official interviews
                    usually happen over email-scheduled calls or enterprise tools
                    (Zoom/Google Meet with company emails), not random Telegram/Discord
                    messages.
                </li>
                <li>
                    <strong>Beware of upfront payments:</strong> No real employer will ask
                    for deposits, training fees, or clearance charges.
                </li>
                <li>
                    <strong>
                        And lastly, if something sounds too good to be true, then it
                        probably is.
                    </strong>
                </li>
            </ul>

            <h2>The Future of Web3 Security and Social Engineering</h2>
            <p>
                Looking ahead, Web3 security must evolve beyond technical fixes.
                Attackers will continue to refine phishing techniques, especially as AI
                makes fake identities, deepfake calls, and personalised lures becoming
                more convincing.
            </p>
            <p>Potential countermeasures could be:</p>
            <ul>
                <li>
                    <strong>Wallet-Level Protections:</strong> Transaction simulation that
                    shows human-readable outcomes before signing.
                </li>
                <li>
                    <strong>AI-Driven Scam Detection:</strong> Automated monitoring of
                    community platforms to detect scam patterns.
                </li>
                <li>
                    <strong>Decentralised Reputation Systems:</strong> On-chain scores
                    that flag suspicious addresses and domains.
                </li>
                <li>
                    <strong>Education &amp; Cultural Shifts:</strong> Projects must
                    normalize skepticism, teaching users to verify before acting.
                </li>
            </ul>
            <p>
                Social engineering will never disappear, but mitigation can become
                embedded at every layer of the ecosystem.
            </p>

            <h2>Best Practices for Web3 Projects &amp; Communities and Users</h2>
            <p>Mitigation requires action from both individuals and organisations.</p>
            <p>
                <strong>For Users:</strong>
            </p>
            <ul>
                <li>Use hardware wallets and multisig for large holdings.</li>
                <li>Always verify domains and community announcements.</li>
                <li>Never share seed phrases or private keys.</li>
                <li>Treat unsolicited job offers and DMs with skepticism.</li>
                <li>Double-check transaction previews and revoke unused allowances.</li>
            </ul>
            <p>
                For hands-on phishing training, try{" "}
                <a href="https://unphishable.io" target="_blank" rel="noreferrer">
                    Unphishable.io
                </a>{" "}
                an interactive site developed by SlowMist, ScamSniffer, and DefiHackLabs
                that walks users through realistic phishing scenarios and teaches how to
                spot social-engineering techniques in web3.
            </p>
            <p>
                <strong>For Projects &amp; Communities:</strong>
            </p>
            <ul>
                <li>
                    Implement official communication policies (e.g., "we never DM first").
                </li>
                <li>Educate users about ongoing scam campaigns.</li>
                <li>Integrate transaction simulation tools into wallets.</li>
                <li>Secure Discord/Telegram with 2FA and role restrictions.</li>
                <li>Proactively monitor and takedown phishing domains.</li>
            </ul>
            <p>
                Collaboration between projects and communities is vital because phishing
                is an ecosystem-wide threat.
            </p>

            <h2>
                Beyond Code: Building Web3 Security Through Awareness and Vigilance
            </h2>
            <p>
                Phishing and social engineering remain the most dangerous threats in
                Web3. not because cryptography is weak, but because human behaviour is
                exploitable. Attackers don't need to crack blockchains; they just need
                to exploit trust, ambition, or fear to convince someone to click, sign,
                or share.
            </p>
            <p>
                For Web3 to thrive, security can't be left to code alone. A truly
                resilient ecosystem requires pairing technical safeguards with human
                awareness and vigilance. Projects must design safer defaults, clearer
                wallet prompts, stronger verification layers, and proactive scam
                detection. While communities must actively educate members, highlight
                social engineering risks and educate about the latest phishing tactics
                and techniques.
            </p>
            <p>
                Awareness is not a secondary defence, it is the first line. Every
                informed user reduces the attacker's pool of potential victims, and
                every vigilant community makes phishing harder to scale.
            </p>
            <p>
                If Web3 is to fulfill its promise of empowering people through
                decentralisation, it must also empower them against attackers. That
                future depends as much on education and shared responsibility as it does
                on innovation in code.
            </p>
        </ArticleTemplate>
    );
};

export default PhishingAttacks;
